// NOTE: This should accept any FormValues but can not infer the type since it is a component
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Except } from '@common/types/common.type';
import { ComponentType, DetailedHTMLProps, FocusEventHandler, Ref, useCallback } from 'react';
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  RefCallBack,
  UseControllerProps,
  UseControllerReturn,
  useFormContext,
} from 'react-hook-form';

// #region Types
export type ExtractHTMLElementByProps<PropsType, FallbackType> =
  PropsType extends DetailedHTMLProps<any, infer ElementType> ? ElementType : FallbackType;

export type WithFormControllerProps<FieldType extends FieldValues> = Partial<
  UseControllerProps<FieldType>
> &
  Partial<Except<UseControllerReturn<FieldType>, 'field'>> &
  Partial<Pick<ControllerRenderProps<FieldType>, 'onChange' | 'value'>> & {
    onBlur?: FocusEventHandler<unknown>;
  };
// #endregion

/**
 * HOC to wrap a component with a form controller.
 *
 * @param Component - The component to wrap.
 * @returns The wrapped component.
 */
export default function withFormController<
  FieldType extends FieldValues = any,
  Props extends WithFormControllerProps<FieldType> = WithFormControllerProps<FieldType>,
  ElementType = ExtractHTMLElementByProps<Props, ComponentType<Props>>,
>(Component: ComponentType<Props>) {
  function WrappedComponent({ ref, ...props }: Props & { ref?: Ref<ElementType> }) {
    const {
      control,
      defaultValue,
      disabled,
      name,
      onBlur,
      onChange,
      rules,
      shouldUnregister,
      value,
    } = props;

    const methods = useFormContext<FieldType>();

    // #region Handlers
    const mergeRef = useCallback(
      (fieldRef: RefCallBack) => {
        const refs = [ref, fieldRef];

        return (instance: ElementType) => {
          refs.forEach((refItem) => {
            if (!refItem) return;

            if (typeof refItem === 'function') refItem(instance);
            // NOTE: ref is reassigned through `current`.
            // eslint-disable-next-line no-param-reassign
            else refItem.current = instance;
          });
        };
      },
      [ref],
    );

    const renderComponent = useCallback(
      ({ field, ...params }: UseControllerReturn<FieldType>) => {
        const {
          onBlur: onFieldBlur,
          onChange: onFieldChange,
          ref: fieldRef,
          value: fieldValue,
        } = field;

        return (
          <Component
            {...(params as Props)}
            {...props}
            ref={mergeRef(fieldRef)}
            onBlur={onBlur ?? onFieldBlur}
            onChange={onChange ?? onFieldChange}
            value={value !== undefined ? value : fieldValue}
          />
        );
      },
      [mergeRef, onBlur, onChange, props, value],
    );
    // #endregion

    return (control || methods) && name ? (
      <Controller
        control={control ?? methods?.control}
        defaultValue={defaultValue}
        disabled={disabled}
        name={name}
        render={renderComponent}
        rules={rules}
        shouldUnregister={shouldUnregister}
      />
    ) : (
      <Component {...(props as Props)} ref={ref} />
    );
  }

  WrappedComponent.displayName = `${Component.displayName ?? 'Anonymous'}WithFormController`;

  return WrappedComponent;
}
